import { useMutation, useQuery } from '@apollo/client';
import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import {
  capitalizeFirstCharacter,
  DisplayErrors,
  InboundOAuthContextProvider,
  PartnerWidgetConfigurationSetting,
} from 'lib-integrations-partner-widgets';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DeepWritable } from 'ts-essentials';
import { v4 as uuid } from 'uuid';

import {
  CreatePartnerSettingsConfigurationArg,
  CreatePartnerSettingsConfigurationResponse,
  createWidgetConfigurationMutation,
  PartnerSettingsConfiguration,
  PartnerSettingsConfigurationSchema,
  ReadPartnerSettingsConfigurationArg,
  ReadPartnerSettingsConfigurationResponse,
  ReadPartnerSettingsConfigurationSchemaArg,
  readPartnerSettingsConfigurationSchemaQuery,
  ReadPartnerSettingsConfigurationSchemaResponse,
  readWidgetConfigurationByIdQuery,
  UpdatePartnerSettingsConfigurationArg,
  UpdatePartnerSettingsConfigurationResponse,
  updateWidgetConfigurationMutation,
} from 'apis/partnerIntegrations';
import { AppModalBase } from 'app/types/appModal.types';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import { ThirdPartyIntegrationAuthorization } from './components/authorization/thirdPartyIntegrationAuthorization';
import { ThirdPartyMarketplaceAppSetup } from './components/marketplaceAppSetup/thirdPartyMarketplaceAppSetup';
import { ThirdPartySettingsConfiguration } from './components/settingsConfiguration/thirdPartySettingsConfiguration';
import styles from './thirdPartyIntegrationConfigurationModal.module.scss';
import {
  ThirdPartyIntegrationConfigurationModalArg,
  ThirdPartyIntegrationConfigurationModalResult,
} from './thirdPartyIntegrationConfigurationModal.types';

export type SettingsConfigurationFormValues = Record<
  string,
  PartnerWidgetConfigurationSetting & {
    isEditing?: boolean;
  }
>;

/**
 * A modal for configuring 3rd party integrations. This is a wrapper for
 * {@link ThirdPartyIntegrationConfigurationModalImplementation} to provide the
 * GraphQL client inside the modal implementation to support queries and
 * mutations against the partners-api-gateway.
 */
export const ThirdPartyIntegrationConfigurationModal = createAppModal(
  { type: 'app:thirdPartyIntegrationConfiguration' },
  ({ isOpen, modal, hide, meta }): JSX.Element => {
    return (
      <ThirdPartyIntegrationConfigurationModalImplementation
        isOpen={isOpen}
        modalArg={modal.arg}
        meta={meta}
        hide={hide}
      />
    );
  },
);

type ThirdPartyIntegrationConfigurationModalImplementationProps = {
  isOpen: boolean;
  modalArg: ThirdPartyIntegrationConfigurationModalArg;
  meta?: AppModalBase<
    'app:thirdPartyIntegrationConfiguration',
    ThirdPartyIntegrationConfigurationModalArg,
    ThirdPartyIntegrationConfigurationModalResult
  >['meta'];
  hide: VoidFunction;
};

/**
 * A modal for configuring 3rd party integrations.
 *
 * Integrations that authenticate through OAuth are created with their name
 * only. The modal then moves on to an authorization step, so the user can
 * authorize the new integration before its id is handed back to the caller.
 * Integrations that are set up through a marketplace app are not created here
 * at all but by pairing through that app.
 */
export const ThirdPartyIntegrationConfigurationModalImplementation = ({
  isOpen,
  modalArg,
  meta,
  hide,
}: ThirdPartyIntegrationConfigurationModalImplementationProps): JSX.Element => {
  const { integrationType, mode, id: integrationId } = modalArg;
  const isCreateMode = mode === 'create';
  const hasNoIntegrationToUpdate = !integrationId && mode === 'update';
  const modalTitle = `${
    mode === 'create' ? 'Create' : 'Update'
  } ${capitalizeFirstCharacter(integrationType)} Integration`;

  const [createdIntegrationId, setCreatedIntegrationId] = useState<string>();

  const formId = uuid();
  const notify = useNotifications();
  const [integration, setIntegration] = useState<
    PartnerSettingsConfiguration | undefined
  >();

  const {
    data: schemaData,
    loading: isSchemaLoading,
    error: schemaError,
  } = useQuery<
    ReadPartnerSettingsConfigurationSchemaResponse,
    ReadPartnerSettingsConfigurationSchemaArg
  >(readPartnerSettingsConfigurationSchemaQuery, {
    variables: { integrationType },
  });

  const schema = useMemo(
    () =>
      (schemaData?.Management.settingsConfigurationSchema ??
        []) as DeepWritable<PartnerSettingsConfigurationSchema[]>,
    [schemaData],
  );

  const visibleSchema = useMemo(
    () =>
      isCreateMode ? schema.filter((field) => !field.hideOnCreate) : schema,
    [schema, isCreateMode],
  );

  const requiresAuthorization = schema.some((field) => field.type === 'oauth');
  const marketplaceAppField = isCreateMode
    ? schema.find((field) => field.type === 'marketplaceApp')
    : undefined;

  const { loading: isIntegrationLoading, error: integrationError } = useQuery<
    ReadPartnerSettingsConfigurationResponse,
    ReadPartnerSettingsConfigurationArg
  >(readWidgetConfigurationByIdQuery, {
    skip: !integrationId,
    variables: {
      id: integrationId as string,
    },
    onCompleted(data) {
      const integrationData = data.Management.widgetConfigurations;
      setIntegration(integrationData[0]);
    },
  });

  const formValues = useMemo<SettingsConfigurationFormValues>(
    () => ({
      ...Object.fromEntries(
        visibleSchema.map((field) => [
          field.key,
          {
            key: field.key,
            secret: field.secret,
            value: integration?.settings.find(
              (setting) => setting.key === field.key,
            )?.value,
            isEditing: isCreateMode || !field.secret,
          },
        ]),
      ),
      name: { key: 'name', value: integration?.name ?? '', secret: false },
    }),
    [visibleSchema, integration, isCreateMode],
  );

  const form = useForm<SettingsConfigurationFormValues>({
    values: formValues,
  });

  const resolveModalResponse = useCallback(
    (result: ThirdPartyIntegrationConfigurationModalResult) => {
      meta?.resolve(result);

      hide();
    },
    [meta, hide],
  );

  const [createConfiguration, { loading: isCreatingWidgetConfiguration }] =
    useMutation<
      CreatePartnerSettingsConfigurationResponse,
      CreatePartnerSettingsConfigurationArg
    >(createWidgetConfigurationMutation, {
      onError: (err) => {
        notify(
          {
            title: 'Error while creating the settings configuration.',
            text: err.message,
          },
          NotificationType.Error,
        );
      },
    });

  const [updateConfiguration, { loading: isUpdatingWidgetConfiguration }] =
    useMutation<
      UpdatePartnerSettingsConfigurationResponse,
      UpdatePartnerSettingsConfigurationArg
    >(updateWidgetConfigurationMutation, {
      onError: (err) => {
        notify(
          {
            title: 'Error while updating the settings configuration.',
            text: err.message,
          },
          NotificationType.Error,
        );
      },
      onCompleted: (result) => {
        const configuration = result.Management.updateWidgetConfiguration;

        notify(
          {
            title: 'Success',
            text: `"${configuration.name}" has been updated.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        resolveModalResponse({
          isSuccessful: true,
          integrationId: configuration.id,
        });
      },
    });

  const isLoading =
    isCreatingWidgetConfiguration ||
    isUpdatingWidgetConfiguration ||
    isIntegrationLoading ||
    isSchemaLoading;

  const handleCreated = (
    result: CreatePartnerSettingsConfigurationResponse,
  ) => {
    const configuration = result.Management.createWidgetConfiguration;

    notify(
      {
        title: 'Success',
        text: `"${configuration.name}" has been created.`,
        icon: 'i-done',
        time: 5000,
      },
      NotificationType.Success,
    );

    if (requiresAuthorization) {
      setCreatedIntegrationId(configuration.id);

      return;
    }

    resolveModalResponse({
      isSuccessful: true,
      integrationId: configuration.id,
    });
  };

  const onSubmit = (formData: SettingsConfigurationFormValues) => {
    const settings = Object.entries(formData)
      .filter(([key, _]) => key !== 'name')
      .map(([key, setting]) => ({
        key,
        value: setting.value,
        secret: setting.secret,
      }));

    if (isCreateMode) {
      createConfiguration({
        variables: {
          type: integrationType,
          // The `enabled` flag controls whether the integration will be shown
          // in the conversation sidebar or not. It can be
          // changed by an admin in settings/integrations.
          enabled: false,
          name: formData.name.value as string,
          settings,
        },
        onCompleted: handleCreated,
      });
    } else {
      if (integrationId) {
        updateConfiguration({
          variables: {
            id: integrationId,
            enabled: integration?.enabled ?? false,
            ...(formData.name.value && { name: formData.name.value as string }),
            settings: settings.filter((setting) =>
              setting.secret && !setting.value ? false : true,
            ),
          },
        });
      }
    }
  };

  const handleClose = () => {
    if (createdIntegrationId) {
      resolveModalResponse({
        isSuccessful: true,
        integrationId: createdIntegrationId,
      });

      return;
    }

    resolveModalResponse({
      // A marketplace app integration is created once the pairing has been
      // approved, which happens outside of the modal, so the caller should
      // refetch.
      isSuccessful: !!marketplaceAppField,
      integrationId: undefined,
    });
  };

  const renderBody = () => {
    if (marketplaceAppField) {
      return (
        <ThirdPartyMarketplaceAppSetup
          integrationType={integrationType}
          field={marketplaceAppField}
        />
      );
    }

    if (createdIntegrationId) {
      return (
        <ThirdPartyIntegrationAuthorization
          integrationId={createdIntegrationId}
          integrationType={integrationType}
        />
      );
    }

    return (
      <FormProvider {...form}>
        <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
          <FormGrid>
            {isIntegrationLoading ? (
              <Spinner />
            ) : (
              <ThirdPartySettingsConfiguration
                mode={mode}
                integrationType={integrationType}
                integrationId={integrationId}
                schema={visibleSchema}
                isSchemaLoading={isSchemaLoading}
                schemaError={schemaError}
                initialValues={integration}
              />
            )}

            {integrationError && (
              <FormField>
                <DisplayErrors error={integrationError} />
              </FormField>
            )}
          </FormGrid>
        </form>
      </FormProvider>
    );
  };

  const isFormStep = !marketplaceAppField && !createdIntegrationId;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={styles.integrationConfigurationModal}
    >
      <ModalHead
        title={<Text.Heading>{modalTitle}</Text.Heading>}
        onClose={handleClose}
      />

      <InboundOAuthContextProvider>
        <ModalBody>{renderBody()}</ModalBody>
      </InboundOAuthContextProvider>

      {isFormStep ? (
        <ModalFoot
          confirmName="Save"
          cancelName="Cancel"
          cancelOnClick={handleClose}
          confirmOnClick={form.handleSubmit(onSubmit)}
          confirmProps={{
            type: 'submit',
            form: formId,
            isLoading,
            disabled:
              isLoading || hasNoIntegrationToUpdate || !form.formState.isDirty,
          }}
        />
      ) : (
        <ModalFoot
          confirmName={marketplaceAppField ? 'Close' : 'Done'}
          confirmOnClick={handleClose}
        />
      )}
    </Modal>
  );
};
